import { useCallback, useEffect, useState } from 'react'
import { populationsApi } from '@/lib/api'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import { toast as notify } from 'sonner'

export type SharingMode = 'private' | 'shared' | 'organization' | 'marketplace' | 'published'

export interface Collaborator {
  id: string
  userId: string
  role: string
  /** 'internal' = same-org; 'external_invite' = cross-org partner */
  source?: string
  name: string | null
  email: string | null
  createdAt: string
}

export function useAudienceSharing(audienceId: string | null) {
  const [sharingMode, setSharingMode] = useState<SharingMode>('private')
  const [collaborators, setCollaborators] = useState<Collaborator[]>([])
  const [guestChatEnabled, setGuestChatEnabled] = useState(false)
  const [guestChatCap, setGuestChatCap] = useState(25)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!audienceId) {
      setLoading(false)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const data = await populationsApi.getSharing(audienceId)
        if (cancelled) return
        setSharingMode(data.sharingMode as SharingMode)
        setCollaborators(data.collaborators)
        setGuestChatEnabled(data.guestChatEnabled === true)
        setGuestChatCap(data.guestChatCap ?? 25)
      } catch {
        // Sharing may not be available for all audiences
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [audienceId])

  const updateMode = useCallback(async (mode: SharingMode): Promise<boolean> => {
    if (!audienceId) return false
    setSaving(true)
    try {
      await populationsApi.updateSharing(audienceId, { sharingMode: mode })
      track(AnalyticsEvents.AUDIENCE_SHARING_MODE_UPDATED, {
        audience_id: audienceId,
        sharing_mode: mode,
      })
      setSharingMode(mode)
      const messages: Record<SharingMode, string> = {
        marketplace: 'Audience published to marketplace',
        organization: 'Audience shared with your organization',
        shared: 'Audience link sharing enabled',
        published: 'External sharing enabled',
        private: 'Audience set to private',
      }
      notify.success(messages[mode])
      return true
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to update sharing settings'
      notify.error(msg)
      return false
    } finally {
      setSaving(false)
    }
  }, [audienceId])

  const updateGuestChat = useCallback(async (enabled: boolean, cap: number) => {
    if (!audienceId) return
    setSaving(true)
    const previousEnabled = guestChatEnabled
    const previousCap = guestChatCap
    setGuestChatEnabled(enabled)
    setGuestChatCap(cap)
    try {
      await populationsApi.setGuestChat(audienceId, enabled, cap)
      notify.success('Audience chat access updated')
    } catch {
      setGuestChatEnabled(previousEnabled)
      setGuestChatCap(previousCap)
      notify.error('Failed to update audience chat access')
    } finally {
      setSaving(false)
    }
  }, [audienceId, guestChatEnabled, guestChatCap])

  const toggleGuestChat = useCallback(async () => {
    await updateGuestChat(!guestChatEnabled, guestChatCap)
  }, [guestChatEnabled, guestChatCap, updateGuestChat])

  const updateGuestChatCap = useCallback(async (cap: number) => {
    await updateGuestChat(guestChatEnabled, cap)
  }, [guestChatEnabled, updateGuestChat])

  const addCollaborator = useCallback(async (userId: string, role?: 'collaborator' | 'manager') => {
    if (!audienceId) return
    try {
      const result = await populationsApi.addCollaborator(audienceId, userId, role)
      track(AnalyticsEvents.AUDIENCE_COLLABORATOR_ADDED, { audience_id: audienceId })
      setCollaborators((prev) => [...prev, result])
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to add collaborator'
      notify.error(msg)
    }
  }, [audienceId])

  const removeCollaborator = useCallback(async (collaboratorId: string) => {
    if (!audienceId) return
    try {
      await populationsApi.removeCollaborator(audienceId, collaboratorId)
      track(AnalyticsEvents.AUDIENCE_COLLABORATOR_REMOVED, { audience_id: audienceId })
      setCollaborators((prev) => prev.filter((c) => c.id !== collaboratorId))
    } catch {
      notify.error('Failed to remove collaborator')
    }
  }, [audienceId])

  const updateCollaboratorRole = useCallback(async (collaboratorId: string, role: 'collaborator' | 'manager'): Promise<boolean> => {
    if (!audienceId) return false
    try {
      await populationsApi.updateCollaboratorRole(audienceId, collaboratorId, role)
      setCollaborators((prev) => prev.map((c) => c.id === collaboratorId ? { ...c, role } : c))
      return true
    } catch {
      notify.error('Failed to update role')
      return false
    }
  }, [audienceId])

  return {
    sharingMode,
    collaborators,
    guestChatEnabled,
    guestChatCap,
    loading,
    saving,
    updateMode,
    toggleGuestChat,
    updateGuestChatCap,
    addCollaborator,
    removeCollaborator,
    updateCollaboratorRole,
  }
}
